import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { MdOutlineWarningAmber } from 'react-icons/md'
import { useLocation } from 'react-router-dom'

import type { ReactElement } from 'react'

import Banner from '~/components/Banner'
import Button from '~/components/Buttons/Button'
import ModalDialog from '~/components/ModalDialog'
import { SESSION_STORAGE } from '~/const'
import { useAppSummary } from '~/providers/AppSummary'
import dayjs from '~/utils/dayjs'
import { getDeprecatedService } from '~/utils/deprecation'

import './styles.scss'

interface Props {
  className?: string
  /** It only handles the services without banner (see IDeprecatedService.popupOnly), with their modal */
  popupOnly?: boolean
}

/**
 * It warns that the current page belongs to a deprecated service and links to the page that replaces it: a banner on
 * each page, plus a modal on the first page of the service opened in the tab
 */
const DeprecationBanner = ({ className, popupOnly = false }: Props): ReactElement | null => {
  /** Local state */

  const { pathname } = useLocation()

  const { appKey, deviceType } = useAppSummary()

  const [isModalOpen, setIsModalOpen] = useState(false)

  // NOTE: the banners of the pages and the modal of the app shell each handle their own services
  const deprecatedService = useMemo(() => {
    const service = getDeprecatedService(pathname)

    return service && !!service.popupOnly === popupOnly ? service : undefined
  }, [pathname, popupOnly])

  const redirectUrl = useMemo(
    () => deprecatedService?.getRedirectUrl({ appKey, deviceType, pathname }),
    [appKey, deprecatedService, deviceType, pathname]
  )

  /** Effects */

  // NOTE: once per tab session and service, so that browsing the pages of the service does not reopen it
  useEffect(() => {
    if (!deprecatedService) return

    const warnedServices: string[] = JSON.parse(
      sessionStorage.getItem(SESSION_STORAGE.DEPRECATION_WARNED_SERVICES) || '[]'
    )

    if (!warnedServices.includes(deprecatedService.name)) {
      sessionStorage.setItem(
        SESSION_STORAGE.DEPRECATION_WARNED_SERVICES,
        JSON.stringify([...warnedServices, deprecatedService.name])
      )
      setIsModalOpen(true)
    }
  }, [deprecatedService])

  /** Handlers */

  const handleCloseModal = () => setIsModalOpen(false)

  const handleOpenRedirectUrl = () => {
    window.open(redirectUrl, '_blank', 'noreferrer')
    setIsModalOpen(false)
  }

  /** Render */

  if (!deprecatedService || !redirectUrl) return null

  const { name, redirectLabel, removalDate } = deprecatedService

  const { origin, pathname: redirectPathname } = new URL(redirectUrl)

  const displayedUrl = `${origin}${redirectPathname}`

  const displayedRemovalDate = dayjs(removalDate).format('dddd, MMMM D, YYYY')

  return (
    <>
      {popupOnly ? null : (
        <Banner className={classNames('deprecation-banner-component', className)} variant="error">
          <MdOutlineWarningAmber className="deprecation-banner-component__icon" size={24} />
          <p className="deprecation-banner-component__text">
            <strong>{name}</strong> is deprecated and will be removed from this intranet on{' '}
            <strong>{displayedRemovalDate}</strong>. Please use it on {redirectLabel} from now on:{' '}
            <a className="deprecation-banner-component__link" href={redirectUrl} rel="noreferrer" target="_blank">
              {displayedUrl}
            </a>
          </p>
        </Banner>
      )}
      <ModalDialog
        btnCancel={
          <Button ghost onClick={handleCloseModal}>
            Stay here for now
          </Button>
        }
        btnConfirm={
          <Button className="deprecation-modal__open-button" onClick={handleOpenRedirectUrl}>
            {`Open in ${redirectLabel}`}
          </Button>
        }
        className="deprecation-modal"
        icon={<MdOutlineWarningAmber className="deprecation-modal__icon" size={65} />}
        isOpen={isModalOpen}
        mainText={`${name} is moving to ${redirectLabel}`}
        onClose={handleCloseModal}
        onConfirm={handleOpenRedirectUrl}
        subText={`It will be removed from this intranet on ${displayedRemovalDate}. Please use it on ${redirectLabel} from now on.`}
      />
    </>
  )
}

export default DeprecationBanner
